import { z } from 'zod';

/**
 * The generative-UI contract the agent emits (`content` parts on a message) and
 * the SolidJS PWA renders — settings/builder/billing/referral surfaces plus
 * ask-user forms (see docs/idea/agent.md, docs/architecture/design-system.md).
 *
 * This is a SKELETON: each variant lands fully with its phase. The union is
 * REAL and extensible per OCP — a new surface = a new member in the
 * discriminated union keyed on `kind`, with NO change to existing renderers or
 * callers. Keep every variant a `z.object` with a literal `kind`.
 */

/** Which modal surface the agent asks the client to open. */
export const UiSurface = z.enum(['settings', 'builder', 'billing', 'referral']);
export type UiSurface = z.infer<typeof UiSurface>;

/**
 * A single typed field in a `form` directive — rendered by `packages/ui` Form
 * controls (Input/Select/Toggle/Slider/RadioGroup). Discriminated on `type` so
 * a new control = a new member, no renderer fork.
 */
export const UiFormField = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text'),
    name: z.string().min(1),
    label: z.string(),
    placeholder: z.string().optional(),
    required: z.boolean().default(false),
  }),
  z.object({
    type: z.literal('textarea'),
    name: z.string().min(1),
    label: z.string(),
    placeholder: z.string().optional(),
    required: z.boolean().default(false),
  }),
  z.object({
    type: z.literal('number'),
    name: z.string().min(1),
    label: z.string(),
    min: z.number().optional(),
    max: z.number().optional(),
    required: z.boolean().default(false),
  }),
  z.object({
    type: z.literal('select'),
    name: z.string().min(1),
    label: z.string(),
    options: z.array(z.object({ value: z.string(), label: z.string() })).min(1),
    required: z.boolean().default(false),
  }),
  z.object({
    type: z.literal('toggle'),
    name: z.string().min(1),
    label: z.string(),
    default: z.boolean().default(false),
  }),
]);
export type UiFormField = z.infer<typeof UiFormField>;

/** An action button on a card — echoes `action` back to the run when clicked. */
export const UiAction = z.object({
  action: z.string().min(1),
  label: z.string(),
  variant: z.enum(['primary', 'ghost', 'danger']).default('primary'),
});
export type UiAction = z.infer<typeof UiAction>;

/**
 * The directive union. Every member carries a literal `kind` — the client
 * switches on it to pick a renderer.
 */
export const UiDirective = z.discriminatedUnion('kind', [
  /** Open a modal surface (settings/builder/billing/referral). */
  z.object({
    kind: z.literal('modal'),
    surface: UiSurface,
    title: z.string().optional(),
    dismissible: z.boolean().default(true),
  }),
  /** Render a form and park the run as `waiting_input` until answered (ask_user). */
  z.object({
    kind: z.literal('form'),
    id: z.string().min(1),
    title: z.string().optional(),
    fields: z.array(UiFormField).min(1),
    submitLabel: z.string().default('Submit'),
  }),
  /**
   * Ask the user to pick exactly one option — the wizard's `ask_user`
   * describe-or-upload step. The chosen `value` is echoed back to the run, which
   * parks as `waiting_input` until answered (like `form`). Rendered as a
   * radio/segmented control by `packages/ui`.
   */
  z.object({
    kind: z.literal('choice'),
    id: z.string().min(1),
    title: z.string().optional(),
    prompt: z.string().optional(),
    options: z
      .array(
        z.object({
          value: z.string().min(1),
          label: z.string(),
          description: z.string().optional(),
        }),
      )
      .min(2),
  }),
  /**
   * Request a file upload — the wizard's concept-image `upload` step. The client
   * stores the file through the media ingest seam and echoes back the resulting
   * asset id; the run parks as `waiting_input` until it does. `accept`/`maxBytes`
   * are client HINTS — the edge re-validates type + size before storing
   * (docs/mvp-v1.0.0/phase-4-companions; red line 2 — no real/identifiable person).
   */
  z.object({
    kind: z.literal('file'),
    id: z.string().min(1),
    title: z.string().optional(),
    prompt: z.string().optional(),
    accept: z.array(z.string().min(1)).min(1).default(['image/png', 'image/jpeg', 'image/webp']),
    maxBytes: z
      .number()
      .int()
      .positive()
      .default(12 * 1024 * 1024),
  }),
  /** Open the media lightbox over a set of her assets (show_gallery). */
  z.object({
    kind: z.literal('gallery'),
    assetIds: z.array(z.string().min(1)).min(1),
    startIndex: z.number().int().nonnegative().default(0),
  }),
  /** Render a preview/confirm card with optional actions (render_card). */
  z.object({
    kind: z.literal('card'),
    title: z.string(),
    body: z.string().optional(),
    actions: z.array(UiAction).default([]),
  }),
  /** A transient notification (quota push, job done, error). */
  z.object({
    kind: z.literal('toast'),
    level: z.enum(['info', 'success', 'warning', 'error']),
    message: z.string().min(1),
  }),
]);
export type UiDirective = z.infer<typeof UiDirective>;
